import { Link, Popover } from '@mui/material';
import { css, styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { Dispatch, SetStateAction } from 'react';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { QList, QListItemText, QListItemButton } from '@kw/quokka-ui';
import { sendHttpRequest } from '../../utils/network.service';
import { LaunchDarklyFlags } from '../../launch-darkly';

interface PopoverProps {
  policy: any;
  policyId: string;
  anchorEl: HTMLElement;
  setPolicyId: React.Dispatch<number>;
  setAnchorEl: React.Dispatch<HTMLElement>;
  handleClose: (setPolicyId: React.Dispatch<number>, setAnchorEl: React.Dispatch<HTMLElement>) => void;
  mutate?: () => void;
  selected?: string[];
  setSelected?: Dispatch<SetStateAction<string[]>>;
}

export function PoliciesTableAssociatedRulesPopover({ policy, policyId, anchorEl, setPolicyId, setAnchorEl, handleClose }: PopoverProps) {
  const rulesOpen = Boolean(anchorEl);
  const rulesId = rulesOpen ? 'simple-popover' : undefined;

  return (
    <StyledPopover
      id={rulesId}
      open={policyId === policy.id}
      anchorEl={anchorEl}
      onClose={() => handleClose(setPolicyId, setAnchorEl)}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <div className="popover-text">
        {policy.policyRules.map(policyRule => policyRule.rule.name).join(', ')}
        <StyledLink onClick={() => handleClose(setPolicyId, setAnchorEl)}> Show less</StyledLink>
      </div>
    </StyledPopover>
  );
}

export function PoliciesTableMorePopover({
  policy,
  policyId,
  anchorEl,
  setPolicyId,
  setAnchorEl,
  handleClose,
  mutate,
  selected,
  setSelected,
}: PopoverProps) {
  const navigate = useNavigate();
  const options = ['Edit', 'Delete', 'Duplicate'];
  const moreOpen = Boolean(anchorEl);
  const moreId = moreOpen ? 'simple-popover' : undefined;

  const { enqueueSnackbar } = useSnackbar();
  const flags: LaunchDarklyFlags['flags'] = useFlags();

  const handlePopoverListItemClick = async (value: 'Edit' | 'Delete' | 'Duplicate') => {
    if (value === 'Edit') {
      navigate(`/policies/edit/${policyId}`);
    }

    if (value === 'Delete') {
      const path = `${process.env.EVAL_SERVICE_URL}/eval/policies/policy/${policyId}`;

      await sendHttpRequest({
        path,
        method: 'DELETE',
        successMessage: 'Successfully deleted policy',
        errorMessage: 'Error deleting policy',
        enqueueSnackbar,
      });

      removeFromSelected(selected, policyId);
      mutate();
    }

    if (value === 'Duplicate') {
      const path = `${process.env.EVAL_SERVICE_URL}/eval/policies/duplicate/${policyId}`;

      await sendHttpRequest({
        path,
        method: 'POST',
        successMessage: 'Successfully duplicated policy',
        errorMessage: 'Error duplicating policy',
        enqueueSnackbar,
      });

      removeFromSelected(selected, policyId);
      mutate();
    }

    handleClose(setPolicyId, setAnchorEl);
  };

  const removeFromSelected = (selectedPolicies: string[], id: string) => {
    setSelected([...selectedPolicies.filter((p: string) => p !== id)]);
  };

  return (
    <StyledPopover id={moreId} open={policyId === policy.id} anchorEl={anchorEl} onClose={() => handleClose(setPolicyId, setAnchorEl)}>
      <StyledQList>
        {options.map((option: 'Edit' | 'Delete' | 'Duplicate') => (
          <QListItemButton autoFocus onClick={() => handlePopoverListItemClick(option)} key={option}>
            <QListItemText primary={option} />
          </QListItemButton>
        ))}
      </StyledQList>
    </StyledPopover>
  );
}

const StyledLink = styled(Link)`
  cursor: pointer;
  margin-left: 2px;
`;

const StyledQList = styled(QList)`
  padding-top: 0;
  padding-bottom: 0;
`;

const rulesPopoverStyles = css`
  & div.popover-text {
    max-width: 300px;
    font-size: 0.875rem;
    padding: 12px;
    a {
      text-decoration: none;
      &:hover {
        text-decoration: underline;
      }
    }
  }
`;

const StyledPopover = styled(Popover)`
  ${rulesPopoverStyles}
`;
